import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ElementType, type ReactNode } from 'react';

type Gap = 'none' | '1' | '2' | '3' | '4' | '5' | '6' | '8' | '10' | '12';
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around';

const alignMap: Record<Align, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};
const justifyMap: Record<Justify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
};
const gapValue = (g: Gap) => (g === 'none' ? '0' : g);

const Flex = chakra('div', { base: { display: 'flex', minWidth: 0 } });

interface StackProps extends React.HTMLAttributes<HTMLElement> {
  as?: ElementType;
  gap?: Gap;
  align?: Align;
  justify?: Justify;
  children?: ReactNode;
}

/** Vertical rhythm — children stacked in a column with a token gap. */
export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  { as, gap = '4', align = 'stretch', justify = 'start', ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'div'> = {
    flexDirection: 'column',
    gap: gapValue(gap),
    alignItems: alignMap[align],
    justifyContent: justifyMap[justify],
  };
  return <Flex as={as} ref={ref} {...sx} {...rest} />;
});

interface ClusterProps extends StackProps {
  /** Wrap onto multiple lines when space runs out (default true). */
  wrap?: boolean;
}

/** Horizontal grouping — inline items (buttons, chips, meta) with a token gap. */
export const Cluster = forwardRef<HTMLDivElement, ClusterProps>(function Cluster(
  { as, gap = '3', align = 'center', justify = 'start', wrap = true, ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'div'> = {
    flexDirection: 'row',
    flexWrap: wrap ? 'wrap' : 'nowrap',
    gap: gapValue(gap),
    alignItems: alignMap[align],
    justifyContent: justifyMap[justify],
  };
  return <Flex as={as} ref={ref} {...sx} {...rest} />;
});

const GridBase = chakra('div', { base: { display: 'grid', minWidth: 0 } });

interface GridProps extends React.HTMLAttributes<HTMLElement> {
  as?: ElementType;
  /** Fixed column count. */
  columns?: number;
  /** Or: auto-fit columns each at least this wide (responsive). Overrides `columns`. */
  minChildWidth?: string;
  gap?: Gap;
  children?: ReactNode;
}

/** Two-dimensional layout with either a fixed column count or responsive auto-fit. */
export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { as, columns = 2, minChildWidth, gap = '4', ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'div'> = {
    gap: gapValue(gap),
    gridTemplateColumns: minChildWidth
      ? `repeat(auto-fit, minmax(min(${minChildWidth}, 100%), 1fr))`
      : `repeat(${columns}, minmax(0, 1fr))`,
  };
  return <GridBase as={as} ref={ref} {...sx} {...rest} />;
});
